// =============================================================================
// ui/tabs/DayOverview.tsx — one day, end to end.
// -----------------------------------------------------------------------------
// Eight sections, in beta's order: nav · scorecard · INTRADAY (curve + prop
// rails) · PERFORMANCE (strip) · BY ACCOUNT · BREAKDOWNS · TRADES.
//
// ── 🚨 FOUR POPULATIONS, AND THEY DISAGREE ON PURPOSE (D77/D80) ──
//
// The densest instance in the client. `domain/dayReport.ts` documents the split
// and takes both sets explicitly so it is visible at the call site:
//
//   dayTrades   every trade of the day   → the KPIs, the curve, CAPTURE and
//                                            Controllable Losses
//   counted     …that count for metrics  → Net P&L, give-back, Avg Time
//   railAccts   active ∩ the filter      → the prop rails
//   allActive   EVERY active account     → the by-account table, filter ignored
//
// ⚠ The last one is not an oversight. Beta lists every active account and marks
// the ones that sat out `N/A`, because absence is a fact about the day — a
// five-account trader whose table silently shrank to the busy accounts would
// have no way to notice one was flat.
//
// ── ⚠ USD ONLY, LIKE EVERY OTHER TAB IN THIS CLIENT ──
//
// Beta runs this whole screen through the global P&L-unit toggle. That toggle is
// not ported yet, and inventing it here would make one tab answer in points
// while the Dashboard beside it answers in dollars. The domain layer is already
// unit-aware, so wiring the toggle later is a prop, not a rebuild.
//
// ⚠ STRUCTURE, NOT THE DESIGN SYSTEM. Same standing caveat as the rest of ui/.
// =============================================================================

import { useEffect, useCallback, useId, useMemo, useState, type ReactElement } from 'react';
import { useAccounts } from '../state/accounts';
import { useContracts } from '../state/contracts';
import { usePnlUnit } from '../state/usePnlUnit';
import { tradeValue } from '../../selectors/pnlUnit';
import type { PnlUnit } from '../../selectors/pnlUnit';
import { useDayBaseline } from '../state/useDayBaseline';
// ⚠ `useActiveFacets` is deliberately NOT imported — see `dayTrades` below.
import { useFilters } from '../state/filters';
import { usePatterns } from '../state/patterns';
import { usePreferences } from '../state/preferences';
import { useSnapshot } from '../state/snapshot';
import { useShare } from '../state/share';
import { activeAccountLabel } from '../../selectors/share';
import { useRange } from '../state/range';
import { useTradeRows } from '../state/useTradeRows';
import { BarCard } from './BarCard';
import { TradeTable, makeAccountLabeller } from './TradeTable';
import { TradeDetail } from './TradeDetail';
import { fullDayLabel, money, moneyPlain, num, pct, pnlClass, unitValue, unitPlain, unitCompact } from '../format';
import {
  tradesCountingForMetrics, tradesForSelection, type Account,
} from '../../selectors/accounts';
import { mfeCapture } from '../../selectors/capture';
import { sessionPnl } from '../../selectors/dashBreakdowns';
// ⚠ The leak CATALOGUE, only to turn a rule's `leakId` into the name a reader
// knows it by. See `ActiveRules` — the id is already on the rule and was simply
// never resolved, which is why the strip showed a commitment without saying what
// habit it was about.
import { leakById } from '../../selectors/leaks';
import {
  averageDayPnl, avgMinutesInTrade, captureTone, dayAccountRows, dayScorecard,
  activeLeakRules, daysTraded, intradayCurve, minsToHm, niceTicks,
  planRecapLines, sessionTzLabel, stepAndHold,
  type DayAccountRow, type IntradayCurve, type LeakRule,
} from '../../selectors/dayReport';
import { useDayJournal } from '../state/useDayJournal';
import { selectKpis } from '../../selectors/kpis';
import {
  computeConsistencyMath, computeProfitTargetProgress, tradesInAttempt,
} from '../../selectors/propReport';
import {
  addDays, isWeekendIso, nextSessionDate, previousSessionDate,
} from '../../selectors/journal';
import type { JournalEntry } from '../../selectors/journal';
import type { TradeTableRow } from '../../io/sync/trades';

export interface DayOverviewTabProps {
  /**
   * The day to open on — set when the user clicks a Calendar cell.
   *
   * ⚠ AN INITIAL VALUE, NOT A CONTROLLED PROP. It seeds `picked` once, and the
   * shell remounts this tab with a `key` when the requested day changes. Making
   * it controlled would mean the ‹ › arrows had to report upward and wait to be
   * told what they did, which is more machinery than a date needs — and an
   * effect that copies it into state after render is the D86 race.
   */
  initialDate?: string | null;
}

/** ⚠ Beta's id, so a saved capture is comparable between the two clients. */
const DAYVIEW_SNAPSHOT_REGION_ID = 'dayview-snapshot-region';

export function DayOverviewTab({ initialDate = null }: DayOverviewTabProps = {}) {
  const { mode, today } = useRange();
  const { accounts } = useAccounts();
  const { contracts } = useContracts();
  const { patterns } = usePatterns();
  const { prefs } = usePreferences();
  // The global display unit. This tab converts fully — selector AND
  // formatters — which is the only safe pairing (#44).
  const { unit } = usePnlUnit();

  /**
   * One trade’s value in the display unit, for the table’s P&L column.
   *
   * ⚠ The table takes this FUNCTION rather than the contract catalogue — it has
   * no other reason to know what a tick size is. Null where the trade has no
   * value in the unit (no stop, no R), which the cell renders as an em-dash.
   */
  const valueOf = useCallback(
    // 🚨 NULL IN, NULL OUT. `tradeValue` coerces a null P&L to 0 because that is
    // right for SUMMING — a trade with no result contributes nothing. It is wrong
    // for a CELL: an open or unpriced trade would render $0.00 and read as a
    // scratch, which is a different and confident claim. Caught by the table’s
    // own test, which has asserted the em-dash since the port began.
    (t: TradeTableRow) => (t.netPnl == null ? null : tradeValue(t as never, unit, contracts ?? [])),
    [unit, contracts],
  );

  /**
   * The same trade's value, for SUMMING rather than for a cell.
   *
   * 🚨 NOT `valueOf`, AND THE DIFFERENCE IS LOAD-BEARING. That one is null-in
   * null-out so the table can render an em-dash for an unpriced trade. Summing
   * with it would drop a trade that HAS points but no booked P&L out of the
   * points total — and, worse, delete that trade's whole row from the DOLLAR
   * card, because the row only exists if something summed into it.
   *
   * ⚠ This returns null only where beta's does (app/index.html:19254): an
   * uncataloged contract in ticks, or a stop-less trade in R. Never because the
   * net P&L is missing.
   */
  const sumValueOf = useCallback(
    (t: TradeTableRow) => tradeValue(t as never, unit, contracts ?? []),
    [unit, contracts],
  );
  // The global display unit — $ / points / ticks / R. See `UnitPicker`.
  const { accounts: selection } = useFilters();
  const baseline = useDayBaseline();

  const [picked, setPicked] = useState<string | null>(initialDate);
  const snapshot = useSnapshot();
  const share = useShare();
  const [openId, setOpenId] = useState<string | null>(null);

  // ⚠ SNAPPED, not seeded (D86/D87). The anchor is READ and never written, and
  // the weekend snap happens on EVERY path rather than only on the arrows —
  // where a rule is enforced is part of the rule. Typing 2026-08-01 into the
  // date input in session mode has to land on the same day the arrows would.
  //
  // 🔴 BACKWARD, TO THE FRIDAY — AND THE JOURNAL DELIBERATELY GOES THE OTHER WAY.
  // This tab used to snap FORWARD to the Monday, which meant picking a Saturday
  // showed a different day's trades in each client. Beta's Day Overview walks
  // back (app/index.html:28275, `previousSessionDate`).
  //
  // ⚠ D87 IS NOT BROKEN BY THIS, AND THE DISTINCTION IS EASY TO LOSE. D87
  // mandates a FORWARD snap and it is right: it governs `ui/state/useJournal.ts`
  // and cites beta's JOURNAL renderer (index.html:33726), which genuinely does
  // go forward. **Beta disagrees with itself between its own two screens**, and
  // the port copied the Journal's rule onto this tab by mistake.
  //
  // ▶ So the two screens now snap opposite ways ON PURPOSE. A test pins the
  // Journal's direction so nobody "unifies" them later, and the reason a weekend
  // pick means something different on each is worth holding onto: the Day
  // Overview asks "how did that day go?", where the last day that HAPPENED is
  // the Friday; the Journal asks "what am I planning?", where the next day you
  // will trade is the Monday.
  const date = useMemo(() => {
    const base = picked ?? today ?? '';
    if (!base) return '';
    return mode === 'session' && isWeekendIso(base) ? previousSessionDate(base) : base;
  }, [picked, today, mode]);

  /**
   * The long-form date the captured picture is titled with.
   *
   * ⚠ THE RESOLVED DATE, not `picked` — a weekend pick snaps to the next session,
   * and the title has to name the day the numbers under it belong to.
   * ⚠ Built from the parts rather than `new Date(iso)`, which parses a bare ISO
   * date as UTC and can title the picture with yesterday. Beta's format
   * (app/index.html:16020).
   */
  const prettyDate = useMemo(() => {
    const [y, m, d] = date.split('-').map(Number);
    if (!y || !m || !d) return date;
    return new Date(y, m - 1, d).toLocaleDateString('en-US', {
      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
    });
  }, [date]);

  /**
   * ⚠ REGISTERED BY THE TAB, cleared on unmount — the shell's camera has no way
   * to know which tabs are capturable, and beta's hardcoded three-way check in
   * its click handler is the line that gets forgotten when a fourth lands.
   */
  const { setTarget } = snapshot;
  useEffect(() => {
    setTarget({
      regionId: DAYVIEW_SNAPSHOT_REGION_ID, title: prettyDate, label: 'this day',
    });
    return () => setTarget(null);
  }, [setTarget, prettyDate]);

  // ⚠ Reads the RESOLVED date, not `picked` — so a weekend pick loads the
  // journal for the day it snapped to rather than for the Saturday.
  const journal = useDayJournal(date || null);

  const step = useCallback((dir: -1 | 1) => {
    setPicked((cur) => {
      const base = cur ?? today;
      if (!base) return cur;
      if (mode === 'session') {
        return dir === -1 ? previousSessionDate(base) : nextSessionDate(base);
      }
      return addDays(base, dir);
    });
  }, [today, mode]);

  const bounds = useMemo(
    () => (date ? { startDate: date, endDate: date } : null),
    [date],
  );
  // ⚠ Bumped after a SAVE from the trade modal, so the day's numbers re-derive.
  // Without it, grading a trade in the modal leaves the scorecard above it
  // showing the figures from before the edit — and both are on screen at once.
  const [reloadNonce, setReloadNonce] = useState(0);
  const ranged = useTradeRows(bounds, mode, reloadNonce);
  const reload = useCallback(() => setReloadNonce((v) => v + 1), []);

  // ── The four populations ───────────────────────────────────────────────────
  //
  // 🔴 ACCOUNT SELECTION ONLY — THE GLOBAL FACET FILTERS DO NOT APPLY HERE, AND
  // LEAVING THEM OUT IS THE WHOLE POINT (TCR-52).
  //
  // Beta states the rule in its own `FACET_DIMS` block: facets are "applied ONLY
  // through `tradesForActive()` (the 'current view' lens) — the date-bypass
  // callers of `tradesForActiveAccounts()` (Calendar day, journal day, today's
  // PnL) stay a full picture on purpose." Beta's Day Overview is one of those
  // callers (`app/index.html:28486`), so it sees the whole day regardless of
  // what the filter drawer says.
  //
  // ⚠ THIS USED TO CALL `applyFacets` AND IT WAS A PARITY BREAK. With any facet
  // active — instrument, tag, pattern, day-of-week or annotation — this screen
  // silently dropped trades beta counts, and `dayTrades` feeds the scorecard,
  // Net P&L, the intraday curve, P&L by Session and P&L by Pattern. So every
  // number on the page moved, not just the card it was noticed on.
  //
  // 🎯 The distinction is the DATE. A screen that answers "how did the 8th go?"
  // must answer it about the whole 8th; a lens narrowing the current view is a
  // different question. The Dashboard and the Trades tab DO apply facets and are
  // correct to — beta's dashboard is `tradesForActive()`, which includes the
  // facet layer. Calendar and Journal already bypass them here, as beta does.
  const dayTrades = useMemo(() => {
    if (!ranged.rows || !mode || !accounts) return null;
    return tradesForSelection(ranged.rows, selection, accounts);
  }, [ranged.rows, mode, accounts, selection]);

  // ⚠ `tradesCountingForMetrics`, not a bare filter — the exclude-from-metrics
  // rule LIFTS when the selection is excluded-only, so drilling into a SIM
  // account alone still shows its numbers. A hand-rolled filter here would show
  // that user an empty day (see `selectors/accounts.ts`).
  const counted = useMemo(
    () => (dayTrades && accounts ? tradesCountingForMetrics(dayTrades, selection, accounts) : null),
    [dayTrades, selection, accounts],
  );

  const curve = useMemo<IntradayCurve | null>(
    () => (dayTrades && contracts ? intradayCurve(counted ?? [], contracts, unit) : null),
    [counted, dayTrades, contracts, unit],
  );

  const kpis = useMemo(() => {
    if (!dayTrades || !accounts || !contracts) return null;
    return selectKpis({
      trades: dayTrades, accounts, contracts, selection, unit,
    });
  }, [dayTrades, accounts, contracts, selection]);

  /**
   * ── 🚨 CAPTURE EFFICIENCY COUNTS **ALL** THE DAY'S TRADES ──
   *
   * `mfeCapture(dayTrades)`, NOT `mfeCapture(counted)`. Beta's line is
   * `const cap = dashMfeCapture(trades)` — one word, sitting among lines that
   * use `counted`, which is exactly why it was read as `counted` here.
   *
   * ⚠ IT SHIPPED WRONG AND JOSH CAUGHT IT. On 2026-07-29 beta reads **10% ·
   * 10 ≥1R setups** and this read **-1% · 8**: two trades on excluded accounts
   * carry a ≥1R MFE, so dropping them changed both the count and the median.
   * On 2026-07-23 the two agreed at 69%, because none of that day's excluded
   * trades reached 1R — a coincidence that made the tile look verified.
   *
   * ⚠ SO THE SCREEN RUNS **FIVE** POPULATIONS, not the four in this file's
   * header. Capture and Controllable Losses both take every trade; Net P&L,
   * give-back and Avg Time take the counted set.
   */
  const scorecard = useMemo(() => {
    if (!dayTrades || !counted || !contracts) return null;
    return dayScorecard(
      dayTrades, counted, unit, contracts,
      mfeCapture(dayTrades),
      prefs?.controllableMistakeTags ?? null,
    );
  }, [dayTrades, counted, contracts, prefs]);

  /**
   * ── 🚨 THE BASELINE IS A POPULATION TOO, AND THE RAW BOOK IS THE WRONG ONE ──
   *
   * `useDayBaseline` returns EVERY row in the book. Handing that straight to
   * `averageDayPnl` reads a replay account's losses and a SIM account's noise as
   * days you traded — it rendered **avg day −$462** against beta's **−$93** on
   * Josh's own book, because his Replay and Sim1 accounts are in there.
   *
   * It went unnoticed by tsc, by 2,015 green tests and by a plausible-looking
   * number on screen. Only putting it beside beta caught it.
   *
   * ⚠ ONE population for BOTH the numerator and the denominator. Scoping the
   * total but not the day count would divide real P&L by SIM trading days — the
   * flattering direction, and the harder one to notice.
   */
  const comparators = useMemo(() => {
    if (!baseline.rows || !contracts || !mode || !accounts) {
      return { avgDay: null, allTimeWinRate: null };
    }
    const book = tradesCountingForMetrics(baseline.rows, selection, accounts);
    return {
      avgDay: averageDayPnl(book, unit, contracts, mode),
      // ⚠ THE SAME scoped book, so the two comparators cannot disagree about
      // which trades count. Beta derives both from one `computeKpis` call for
      // exactly this reason.
      // 🎯 PINNED TO DOLLARS, AND THAT IS CORRECT RATHER THAN MISSED.
      //
      // Only `.winRate` is read, and `domain/kpis` documents win rate as
      // unit-INVARIANT: it is classified off the $ sign in every unit, because
      // the sign of a result does not change with the unit. Threading the
      // display unit here would recompute the identical number and add a
      // dependency that re-runs this on every unit change for nothing.
      //
      // ⚠ Do not "fix" this line to match the ones above it.
      allTimeWinRate: selectKpis({
        trades: baseline.rows, accounts, contracts, selection, unit: 'usd',
      }).winRate,
    };
  }, [baseline.rows, contracts, mode, accounts, selection]);

  const labelAccount = useMemo(
    () => makeAccountLabeller(accounts ?? []), [accounts],
  );

  // ⚠ The chart's x axis carries the ZONE, as beta's does. A clock time with no
  // zone, on a screen whose day boundary is a user setting, is ambiguous — and
  // this trader's evening trades land either side of it.
  const tzLabel = useMemo(
    () => sessionTzLabel(prefs?.sessionTimezone ?? undefined), [prefs],
  );

  const rules = useMemo(() => activeLeakRules(prefs?.appFlags), [prefs]);

  /**
   * ── 🚨 THE DAY'S TRADES, IN THE ORDER THE DAY HAPPENED ──
   *
   * Beta renders this table with `{ sortable: false }`, and inside
   * `renderTradeTable` that flag does more than hide the header arrows:
   *
   *     const sortedTrades = sortable ? sortTrades(trades, dataView) : trades;
   *
   * So the rows come out in raw `state.trades` order — load order, not time
   * order. On Josh's 2026-08-07 that reads **09:45 · 09:57 · 09:37 · 10:12 ·
   * 09:59 · 09:29**, and the port inherited it exactly.
   *
   * ⚠ It survived because the DEMO day has two trades that happen to be in
   * order. A screenshot of a real day is what showed it, and no test could:
   * asserting "six rows render" passes under any permutation.
   *
   * Time ascending is the only order a single-day view can mean — the chart
   * above it, the streak logic, and the ‹ › walk inside the trade modal all
   * read the day forwards. A trade with no entry time sorts last rather than
   * to the top, where a missing value would otherwise masquerade as the day's
   * first trade.
   */
  const orderedDayTrades = useMemo(() => {
    if (!dayTrades) return null;
    return [...dayTrades].sort((a, b) => {
      const ta = a.timeIn ?? '';
      const tb = b.timeIn ?? '';
      if (!ta && !tb) return 0;
      if (!ta) return 1;
      if (!tb) return -1;
      return ta.localeCompare(tb);
    });
  }, [dayTrades]);

  /**
   * 🚨 WHAT A PUBLIC LINK TO THIS DAY WOULD CONTAIN.
   *
   * ⚠ THE IDS ARE A DISCLOSURE DECISION, NOT A CONVENIENCE. Every trade
   * named here is serialised into the payload, readable in a visitor's Network
   * tab whether or not they scroll to the table — so this is deliberately the
   * SAME list the table below renders, in the same order, and not the day's
   * whole book or the account-filtered superset.
   */
  const { setSource } = share;
  const shareIds = useMemo(
    () => (orderedDayTrades ?? []).map((t) => String(t.id)),
    [orderedDayTrades],
  );
  /**
   * 🚨 EVERY LISTED TRADE'S VALUE IN THE DISPLAY UNIT, computed HERE.
   *
   * ⚠ `valueOf` is this tab's own cell accessor — the very function the table
   * below renders with — so the shared copy prints the same figures as the
   * screen it was taken from. A visitor has no contract catalogue, so ticks and
   * R cannot be worked out on their side at all.
   */
  const shareValues = useMemo(() => {
    const out: Record<string, number | null> = {};
    for (const t of orderedDayTrades ?? []) out[String(t.id)] = valueOf(t);
    return out;
  }, [orderedDayTrades, valueOf]);
  const shareAccountLabel = activeAccountLabel(
    (accounts ?? []).map((a) => ({ id: String(a.id), name: a.name })),
    selection ?? [],
  );
  useEffect(() => {
    setSource({
      kind: 'dayview', title: 'Day Overview', subtitle: prettyDate,
      accountLabel: shareAccountLabel, tradeIds: shareIds,
      unit, valueById: shareValues,
    });
    return () => setSource(null);
  }, [setSource, prettyDate, shareAccountLabel, shareIds, unit, shareValues]);

  /**
   * ── 🚨 A TRADE ON SCREEN THAT NO NUMBER ON SCREEN COUNTS ──
   *
   * The table is the day's FULL trade list; every aggregate above it uses
   * `counted`, which drops accounts flagged "exclude from metrics". Beta does
   * the same and says nothing, so on Josh's 2026-08-07 a **+$296.05** Sim1 row
   * sits under a **-$3** headline — and the only honest reading of that, with
   * no explanation, is that the headline is broken.
   *
   * ⚠ SHOWING THE ROW IS RIGHT. "Here is everything you did today" is what a
   * day view is for, and hiding a trade because of an account setting is how a
   * trader loses track of a position. What was missing is the sentence.
   */
  const excluded = useMemo(() => {
    if (!dayTrades || !counted) return null;
    const hidden = dayTrades.length - counted.length;
    if (hidden <= 0) return null;
    // ⚠ `labelAccount` takes the TRADE, not the id — it falls back to
    // `sourceAccount` for a trade whose account was deleted, which is exactly
    // the row most likely to be sitting here unexplained.
    const countedIds = new Set(counted.map((c) => String(c.id)));
    const names = [...new Set(
      dayTrades
        .filter((t) => !countedIds.has(String(t.id)))
        .map((t) => labelAccount(t)),
    )].filter((s) => s && s !== '—');
    const who = names.length ? names.join(', ') : 'an excluded account';
    const isAre = names.length === 1 || !names.length ? 'is' : 'are';

    // ⚠ TWO DIFFERENT FACTS, NOT ONE WITH A NUMBER SWAPPED. "3 of your 5 trades
    // are not counted" and "NOTHING here is counted" read differently and are
    // shown in different places — the second has to explain why an entire page
    // of blanks is not a fault, so it is a banner at the top rather than a
    // footnote under the table.
    //
    // ⚠ ONLY THE PARTIAL SENTENCE IS BUILT HERE. The total case is worded in
    // the banner's own JSX; writing it in both places is how the two drift into
    // saying slightly different things about one fact.
    return {
      all: counted.length === 0,
      hidden,
      accounts: names,
      partialText: `${hidden} trade${hidden === 1 ? '' : 's'} below ${hidden === 1 ? 'is' : 'are'} on `
        + `${who}, which ${isAre} set to stay out of your metrics — so `
        + `${hidden === 1 ? 'it is' : 'they are'} shown here but counted in nothing above.`,
    };
  }, [dayTrades, counted, labelAccount]);

  /** The beside-the-table note — suppressed when the banner already said it. */
  const excludedNote = excluded && !excluded.all ? excluded.partialText : null;

  if (!date) return <p className="muted">Resolving today…</p>;

  const dayPnl = scorecard?.netPnl ?? 0;
  const n = counted?.length ?? 0;
  /**
   * ── 🚨 "NO TRADES" ON A DAY WITH SEVENTY-FIVE OF THEM ──
   *
   * The page used to gate on `n` — trades that COUNT — so a day whose every
   * trade sits on an excluded account short-circuited to "No trades on
   * 2026-08-05" while 75 trades worth −$1,048 sat behind it. Josh found it on
   * the screen.
   *
   * ⚠ AND THE SENTENCE THAT EXPLAINS IT WAS ALREADY WRITTEN. `excludedNote`
   * above says exactly what is going on — and the gate below it meant it could
   * never render on the one day it mattered most. **Code that is correct and
   * unreachable is the third failure of this shape found in two days.**
   *
   * The question the gate has to ask is "did anything happen today", not "did
   * anything happen that I am allowed to count".
   */
  const anyTrades = (dayTrades?.length ?? 0) > 0;
  /** Trades happened, and not one of them counts anywhere above. */
  const allExcluded = anyTrades && n === 0;

  return (
    <>
      <DayNav
        unit={unit}
        date={date}
        today={today ?? date}
        onStep={step}
        onPick={setPicked}
        onToday={() => setPicked(null)}
        label={fullDayLabel(date)}
        // ⚠ COUNTS THE WHOLE DAY when nothing counts toward the metrics.
        // "0 trades · $0.00" above a page listing 57 of them is the same lie
        // the empty state used to tell, just in smaller type.
        sub={allExcluded
          ? `${dayTrades?.length ?? 0} trade${(dayTrades?.length ?? 0) === 1 ? '' : 's'} · none counted · `
          : `${n} trade${n === 1 ? '' : 's'} · `}
        pnl={dayPnl}
        hasTrades={n > 0}
        pnlHidden={allExcluded}
      />

      {/* ⚠ OUTSIDE the snapshot region, as in beta — it is interactive chrome
          and it is not specific to the day being reviewed, so it does not
          belong in an exported picture of that day.

          🔴 AND `data-noshare`, WHICH IS A DATA-EXPOSURE FIX RATHER THAN
          STYLING (D213). This chip is the behavioural rule the trader is
          currently working on — their own self-diagnosed weakness — and a
          shared report opened with it as the first line. */}
      <div data-noshare style={{ display: 'contents' }}><ActiveRules rules={rules} /></div>

      {ranged.phase === 'failed' ? (
        <div className="card"><p className="red">{ranged.error}</p></div>
      ) : null}

      {/* ⚠ The region is present even with NO trades, mirroring the Dashboard's
          `#dash-snapshot-region` — the camera button in the shell needs
          something to capture on every day, not only on busy ones.

          🔴 AND IT WAS WIRED TO NOTHING UNTIL NOW. The markup was here, the id
          matched beta's, and no tab ever registered it — so the camera was
          permanently unavailable on this screen and this div was dead. #43. */}
      <div id={DAYVIEW_SNAPSHOT_REGION_ID}>
        {/* Rendered only during a capture, by the region that owns it. Beta
            titles this one with the long-form date (app/index.html:16020-16024)
            rather than a range, because a day view is one day.

            ⚠ "Performance Report", NOT "Day Report" — and this was wrong for a
            day. Beta builds all three captions from ONE title bar
            (app/index.html:15965) and writes the same heading on every one, so a
            reader comparing the two clients' day pictures side by side would see
            a difference nothing had declared. The date beside it already says
            which report this is. */}
        {snapshot.capturing === DAYVIEW_SNAPSHOT_REGION_ID ? (
          <div className="snap-title-bar">
            <h1>{prefs?.displayName?.trim() ? `${prefs.displayName.trim()} ` : ''}Performance Report</h1>
            <span className="mono">{prettyDate}</span>
          </div>
        ) : null}
        {!dayTrades ? (
          <p className="muted">Loading…</p>
        ) : !anyTrades ? (
          <div className="card dayview-empty">
            <p className="muted">No trades on {date}.</p>
          </div>
        ) : (
          <>
            {/* 🚨 FIRST, ABOVE EVERY BLANK IT EXPLAINS. Placed here rather than
                beside the table because on this day the blanks ARE the page —
                a reader meeting six empty cards before the reason has already
                concluded the app is broken. */}
            {allExcluded && excluded ? (
              <div className="card dayview-all-excluded" role="status">
                <p>
                  <b>Nothing on this day counts toward your metrics.</b>
                </p>
                {/* ⚠ "All N trades … are on X", not "Every trade — all N of
                    them — is/are on X". The first version agreed the verb with
                    the ACCOUNT count and rendered "all 57 of them are on" under
                    a singular subject. Rewritten so there is no agreement to
                    get wrong, which is a better fix than a cleverer ternary. */}
                <p className="muted">
                  {`All ${excluded.hidden} trade${excluded.hidden === 1 ? '' : 's'} on this day `}
                  {excluded.hidden === 1 ? 'is on ' : 'are on '}
                  <b>{joinNames(excluded.accounts)}</b>
                  {`, which ${excluded.accounts.length === 1 ? 'is' : 'are'} set to stay out of your `}
                  {'metrics. That is why your P&L and every performance figure below are blank — '}
                  {'not because the day was quiet. The trades are listed in full at the foot of '}
                  {'the page.'}
                </p>
                {/* ⚠ NAMES THE TWO THAT STILL READ, rather than claiming
                    everything is blank and being visibly wrong twice on the
                    same screen. Capture Efficiency and Controllable Losses are
                    computed over the WHOLE day rather than the counted set —
                    deliberately, matching beta — because they measure how the
                    trading went, not what it paid, and that is just as true on
                    an account kept out of the totals. */}
                <p className="muted">
                  {'Capture Efficiency and Controllable Losses still read, because they measure '}
                  {'how you traded rather than what you made.'}
                </p>
                {/* ⚠ "MONEY figures", not "every figure", and the difference is
                    a real one. Capture Efficiency reads the WHOLE day rather
                    than the counted set — deliberately, matching beta — so it
                    still shows a number here, and rightly: it measures how much
                    of the move you kept, which is just as true on an account
                    you have excluded from your totals. A banner claiming
                    everything is blank while one tile plainly is not would
                    teach the reader to distrust the banner. */}
                {/* ⚠ THE WAY OUT, AND IT ALREADY EXISTS. `tradesCountingForMetrics`
                    LIFTS the exclusion when the selection is excluded-only — so
                    filtering to these accounts alone makes the whole page
                    compute. Saying so beats adding a toggle that would create a
                    second, contradictory reading of the same day. */}
                <p className="muted">
                  {'To see this day measured anyway, set the account filter to '}
                  {excluded.accounts.length ? <b>{joinNames(excluded.accounts)}</b> : 'those accounts'}
                  {' — with nothing else selected, the exclusion lifts and every card below fills in.'}
                </p>
              </div>
            ) : null}
            <Scorecard
              unit={unit}
              card={scorecard}
              avgDay={comparators.avgDay}
              baselineFailed={!!baseline.error}
              nothingCounted={allExcluded}
            />

            <Divider label="Intraday" />
            <div className="dayview-intraday">
              <IntradayCard curve={curve} tzLabel={tzLabel} unit={unit} />
              {/* 🔴 D213. The Prop Rails card carries the daily loss limit and
                  the profit target IN DOLLARS — effectively the account size —
                  plus payout-consistency figures. `display: contents` keeps the
                  wrapper invisible to layout, so the signed-in grid is unchanged;
                  the capture collapses the 2fr/1fr row to one column once this
                  is gone, or the chart renders at two-thirds width beside a
                  hole. */}
              <div data-noshare style={{ display: 'contents' }}>
              <PropRails
                accounts={accounts}
                selection={selection}
                allRows={baseline.rows}
                curve={curve}
                dayPnlUsd={scorecard?.netPnlUsd ?? 0}
                today={today ?? date}
                mode={mode}
              />
              </div>
            </div>

            <Divider label="Performance" />
            <PerformanceStrip
              k={kpis}
              curve={curve}
              counted={counted ?? []}
              winRateDelta={
                kpis && comparators.allTimeWinRate != null
                  ? kpis.winRate - comparators.allTimeWinRate
                  : null
              }
            />

            {/* 🔴 D213. Every active account BY NAME with its progress toward
                a payout — and it deliberately ignores the account filter, so
                filtering to one account before sharing did not narrow it. */}
            <div data-noshare style={{ display: 'contents' }}>
            <Divider label="By Account" />
            <ByAccount
              unit={unit}
              accounts={accounts}
              dayTrades={ranged.rows}
              contracts={contracts}
              allRows={baseline.rows}
              today={today ?? date}
              mode={mode}
            />
            </div>

            <Divider label="Breakdowns" />
            <div className="dayview-breakdowns">
              {/* 🔴 THESE TWO CARDS STAYED IN DOLLARS ON A PAGE THAT CONVERTS.
                  Switch the app to points and every other number on this screen
                  moved while these two kept their dollar signs — and this tab is
                  on the list of screens that claim to be fully converted, so
                  nothing on screen said otherwise. Beta converts both
                  (app/index.html:28581). ⚠ Both halves move together: the rows
                  are SUMMED in the unit here and the card PRINTS in the unit. */}
              <BarCard
                title="P&L by Session"
                rows={sessionPnl(counted ?? [], (t) => sumValueOf(t as TradeTableRow))}
                empty="No session-tagged trades."
                unit={unit}
              />
              <BarCard
                title="P&L by Pattern"
                rows={patternRows(counted ?? [], patterns, sumValueOf)}
                empty="No pattern-tagged trades."
                unit={unit}
              />
            </div>

            {/* ⚠ NO DIVIDER OF ITS OWN — beta appends this card straight after
                the breakdowns grid, still under the "Breakdowns" heading
                (index.html:28235). It closes the report rather than opening a
                section, which is why it has a card title and not a rule.

                ⚠ INSIDE the snapshot region, also beta's: the plan and the
                recap ARE the day's report content, and an exported picture of
                the day without what the day was for is the least useful half. */}
            {/* 🔴 D213 — and note this is the one exclusion that differs
                between the camera and the link. The plan and the recap ARE the
                day's report content, so they stay in a SNAPSHOT the trader keeps;
                they are the most candid writing in the app — "what broke",
                "lessons", "tomorrow's focus" — so they never go in a link
                a stranger can open. */}
            <div data-noshare style={{ display: 'contents' }}>
              <PlanRecap journal={journal} patterns={patterns} />
            </div>
          </>
        )}
      </div>

      {/* 🔴 THE CAMERA'S STATUS LINE, AND IT WAS ONLY ON THE DASHBOARD.
          snapshot.message carries "Snapshot opened in a new tab", the
          pop-up-blocked notice and every failure — and only the Dashboard
          rendered it. So on this tab a blocked pop-up or a failed raster said
          NOTHING AT ALL: the button un-greys and no picture ever appears, which
          reads as an app that ignored the click.

          ⚠ OUTSIDE the capture region, so the status never appears in the PNG. */}
      {snapshot.message ? (
        <p
          className={`muted snap-status ${snapshot.phase === 'failed' ? 'red' : ''}`}
          role={snapshot.phase === 'failed' ? 'alert' : 'status'}
        >
          {snapshot.message}
        </p>
      ) : null}

      {/* ⚠ 🚨 OUTSIDE the snapshot region, and this MOVED. The port had the
          Trades divider and table inside it; beta closes the region before them
          (index.html:28236-28238) with its own comment saying why — "the Trades
          table is deliberately OUTSIDE the region (raw fill list isn't report
          content)". A snapshot is something Josh posts; a hundred rows of fills
          under the summary is not what he is posting. Caught by diffing the
          region's children against beta's, not by any test. */}
      {/* ⚠ GATED ON `anyTrades`, NOT `n`, AND THAT IS THE SECOND HALF OF THE
          SAME DEFECT. This read `&& n` — the COUNTED set — so on a day where
          every trade sits on an excluded account the list vanished along with
          the statistics, which is the one thing that still had something to
          say. A test written for the banner is what caught it; the banner
          promises "the trades are listed in full at the foot of the page", and
          without this that promise pointed at nothing. */}
      {orderedDayTrades && anyTrades ? (
        /* 🚨 MARKED, AND REMOVED FROM THE CAPTURE. The visitor's page
           re-renders this table from the serialised trades so the rows can
           actually be CLICKED — inside the sandboxed frame nothing can bind to
           them, and a captured copy sits there looking identical and doing
           nothing. Beta's marker class, so a payload is readable by either
           client. */
        <div className="share-trades-table">
          <Divider label="Trades" />
          {/* 🚨 THE ONLY PLACE ON THIS SCREEN WHERE A TRADE APPEARS THAT NO
              NUMBER ABOVE IT COUNTS. See `excludedNote` — the table is the
              unfiltered day on purpose, and until now nothing said so. */}
          {excludedNote ? <p className="muted dayview-excluded-note">{excludedNote}</p> : null}
          <TradeTable
            unit={unit}
            valueOf={valueOf}
            rows={orderedDayTrades}
            kind="annotated"
            mode={mode ?? 'calendar'}
            labelAccount={labelAccount}
            onOpen={setOpenId}
          />
        </div>
      ) : null}

      {openId ? (
        <TradeDetail
          tradeId={openId}
          // ⚠ The ‹ › nav inside the modal walks THIS DAY, in the order the
          // table renders — not the whole book. Handing it every id would let a
          // day review wander off the day being reviewed.
          siblingIds={(dayTrades ?? []).map((t) => String(t.id))}
          onNavigate={setOpenId}
          onSaved={() => { setOpenId(null); reload(); }}
          onDeleted={() => { setOpenId(null); reload(); }}
          onCancel={() => setOpenId(null)}
        />
      ) : null}
    </>
  );
}

/**
 * ⚠ THE SHIPPED DIVIDER, not a new one. Analytics and the Dashboard both use
 * `.an-section-divider`; a second visual language for the same idea on a third
 * tab is exactly the drift the design-system relocation was meant to stop.
 */
/**
 * `["A"]` → `A` · `["A","B"]` → `A and B` · `["A","B","C"]` → `A, B and C`.
 *
 * ⚠ Trivial, and it is here because the first version joined with commas and
 * rendered "on bbc733b3, DEMO26C50" — which reads as a truncated list, i.e. as
 * though a third account had been cut off.
 */
function joinNames(names: readonly string[]): string {
  if (!names.length) return 'an excluded account';
  if (names.length === 1) return names[0] as string;
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

function Divider({ label }: { label: string }) {
  return (
    <div className="an-section-divider">
      <span className="an-label">{label}</span>
      <span className="an-rule" />
    </div>
  );
}

// ── Plan & Recap ─────────────────────────────────────────────────────────────

/**
 * What the day was FOR, under the numbers that say how it went.
 *
 * ⚠ SUPPRESSED WHEN EMPTY, which is beta's behaviour (`dvPlanRecapHtml` returns
 * `''`) and the right one: a card headed "Plan & Recap" containing nothing says
 * the trader planned nothing, and on a day they simply did not journal that is a
 * different claim from the truth.
 *
 * ⚠ A FAILED READ IS NOT AN EMPTY DAY, and it gets a card of its own. Silently
 * rendering nothing would make a broken read indistinguishable from "you did not
 * journal" — the same distinction `useDayJournal` keeps, carried through to the
 * screen rather than dropped at the last step.
 *
 * ⚠ SPACED. Beta appends this straight after the breakdowns grid, which has no
 * bottom margin, so its card sits flush against the two above it with no gap at
 * all. Josh flagged it. `.dayview-planrecap` adds the 14px this tab uses between
 * stacked cards — a deliberate divergence from beta's rendering, not from its
 * structure.
 */
function PlanRecap(
  { journal, patterns }:
  {
    journal: { entry: JournalEntry | null; error: string | null; loading: boolean };
    patterns: readonly { id: string; name: string }[] | null;
  },
) {
  const names = useMemo(
    () => new Map((patterns ?? []).map((p) => [String(p.id), p.name])),
    [patterns],
  );
  const lines = useMemo(
    () => planRecapLines(journal.entry, names),
    [journal.entry, names],
  );

  if (journal.error) {
    return (
      <div className="card dayview-planrecap">
        <h3 className="dash-h3">Plan &amp; Recap</h3>
        <p className="muted">
          The journal entry for this day could not be read, so this is blank for a
          reason that has nothing to do with the day. {journal.error}
        </p>
      </div>
    );
  }
  if (journal.loading || !lines.length) return null;

  return (
    <div className="card dayview-planrecap">
      <h3 className="dash-h3">Plan &amp; Recap</h3>
      <div className="dayview-planrecap-body">
        {lines.map((l) => (
          <div key={l.label}>
            <b>{l.label}:</b> {l.text}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Active rules ─────────────────────────────────────────────────────────────

/**
 * The rule the trader is currently testing, above everything else on the day.
 *
 * ⚠ IT RENDERS NOTHING WHEN THERE IS NO RULE. Beta returns an empty string, and
 * an always-present "no active rule" strip would train the eye to skip the one
 * place this tab puts a commitment the trader made to themselves.
 *
 * ⚠ "Prove it →" is TEXT, not a link. Beta navigates to the leak loop's Prove
 * view, which is not ported — and a control that looks clickable and does
 * nothing is worse than one that plainly does not exist yet.
 */
function ActiveRules({ rules }: { rules: readonly LeakRule[] }) {
  if (!rules.length) return null;
  const first = rules[0] as LeakRule;
  const more = rules.length - 1;
  return (
    <div className="dayview-rules">
      <span className="dayview-rules-k">
        ● {rules.length === 1 ? 'Active rule' : `${rules.length} active rules`}
      </span>
      {/* ⚠ Only the FIRST rule's text, with a count for the rest — beta's
          behaviour. Listing them all turns a one-line commitment into a block
          nobody reads. */}
      <span className="dayview-rules-t">{first.ruleText}</span>
      {/* 🎯 THE LEAK THE RULE IS ABOUT, which beta names here too
          (app/index.html:25262). Without it the strip states a commitment with
          no hint of the habit behind it — "cut the size after two losers" reads
          as a stray note rather than as the answer to something measured.
          ⚠ Nothing new is computed: the id was already on the rule and simply
          never resolved. An unknown id renders nothing rather than a blank. */}
      {leakById(first.leakId)
        ? <span className="muted dayview-rules-leak">{leakById(first.leakId)?.label}</span>
        : null}
      <span className="muted dayview-rules-m">
        {more ? `+${more} more · ` : ''}Prove it →
      </span>
    </div>
  );
}

// ── Nav ──────────────────────────────────────────────────────────────────────

function DayNav(
  { date, today, onStep, onPick, onToday, label, sub, pnl, unit, hasTrades, pnlHidden = false }: {
    date: string; today: string;
    onStep: (dir: -1 | 1) => void;
    onPick: (d: string) => void;
    onToday: () => void;
    label: string; sub: string; pnl: number; unit: PnlUnit; hasTrades: boolean;
    /** Nothing on the day counts, so there is no total to state. */
    pnlHidden?: boolean;
  },
) {
  return (
    <div className="dayview-nav">
      {/* ⚠ `data-noshare` ON THE CONTROLS ONLY, which is beta's boundary
          (app/index.html:28466). The date and the day's total on the right ARE
          the report's own heading and stay; the arrows, the date picker and the
          Coach button are chrome that cannot be pressed on a static page. */}
      <div className="dayview-nav-controls" data-noshare>
        <button type="button" className="btn" title="Previous day" onClick={() => onStep(-1)}>‹</button>
        <button type="button" className="btn" onClick={onToday}>Today</button>
        {/* ⚠ DISABLED at today, not merely clamped. Beta disables it, and the
            difference matters: a button that silently does nothing reads as a
            broken control, while a disabled one says "there is no next day". */}
        <button
          type="button" className="btn" title="Next day"
          disabled={date >= today}
          onClick={() => onStep(1)}
        >›</button>
        <input
          type="date"
          className="dayview-date"
          value={date}
          max={today}
          aria-label="Day"
          onChange={(e) => { if (e.target.value) onPick(e.target.value); }}
        />
        {/* ⚠ Rendered only when the day HAS counted trades, as in beta — a coach
            button on an empty day would spend a metered Claude run to be told
            there is nothing to review. */}
        {hasTrades ? (
          <button type="button" className="btn primary" disabled title="Not ported yet">
            Coach Me — This Day
          </button>
        ) : null}
      </div>
      <div className="dayview-nav-date">
        <div className="dayview-nav-long">{label}</div>
        <div className="muted dayview-nav-sub">
          {/* An em-dash, not $0.00 — see the Scorecard. */}
          {sub}{pnlHidden ? <span className="muted">—</span> : <span className={pnlClass(pnl)}>{unitValue(pnl, unit)}</span>}
        </div>
      </div>
    </div>
  );
}

// ── Scorecard ────────────────────────────────────────────────────────────────

function Tile(
  { label, value, cls, sub, title }:
  { label: string; value: string; cls?: string; sub: string; title?: string },
) {
  return (
    <div className="card dayview-tile" title={title}>
      {/* ⚠ The ⓘ is the AFFORDANCE, and beta has one. A `title` with nothing to
          hover is a tooltip nobody discovers — Give-Back's explanation is the
          longest and least obvious on the tab, and it was invisible. */}
      <div className="muted dayview-tile-k">
        {label}{title ? <span className="dayview-tile-i" aria-hidden="true"> ⓘ</span> : null}
      </div>
      <div className={`dayview-tile-v ${cls ?? ''}`}>{value}</div>
      <div className="muted dayview-tile-sub">{sub}</div>
    </div>
  );
}

function Scorecard(
  { card, avgDay, unit, baselineFailed, nothingCounted }:
  {
    card: ReturnType<typeof dayScorecard> | null;
    avgDay: number | null;
    unit: PnlUnit;
    baselineFailed: boolean;
    /** Trades happened, but not one of them counts toward the metrics. */
    nothingCounted: boolean;
  },
) {
  if (!card) return null;

  /**
   * 🚨 An em-dash, not `$0`. A day where nothing counted is not a day where
   * you made nothing — and "$0" under a page listing 57 trades reads as the
   * app having lost them.
   *
   * ⚠ CAPTURE EFFICIENCY IS DELIBERATELY EXEMPT and still shows a figure. It
   * reads the whole day rather than the counted set (beta does the same), and
   * that is right: it is a PROCESS measure — how much of the move you kept —
   * and the answer is just as true on an account you have excluded from your
   * money totals. It is the one number on this screen that survives.
   */
  const dashMoney = (v: string): string => (nothingCounted ? '—' : v);

  // ⚠ EMPTY, not "avg day $0", when the baseline is unknown. Zero is a real and
  // specific claim — a breakeven trader — and printing it under a winning day
  // reads as "you doubled your usual".
  const netSub = avgDay == null
    ? (baselineFailed ? 'comparison unavailable' : '')
    : `avg day ${unitCompact(avgDay, unit)}`;

  return (
    <div className="dayview-scorecard">
      <Tile
        label="Net P&L" value={dashMoney(unitCompact(card.netPnl, unit))}
        cls={nothingCounted ? '' : pnlClass(card.netPnl)}
        sub={nothingCounted ? 'nothing counted today' : netSub}
      />
      <Tile
        label="Capture Efficiency"
        value={card.capturePct == null ? '—' : `${Math.round(card.capturePct)}%`}
        cls={captureTone(card.capturePct)}
        sub={card.captureN
          ? `median of MFE · ${card.captureN} ≥1R setup${card.captureN === 1 ? '' : 's'}`
          : 'no ≥1R MFE trades'}
      />
      <Tile
        label="Cost of Give-Back"
        // ⚠ The `≈` is a property of the SUM. A total mixing a measured figure
        // with an upper bound is an upper bound, and hiding that would present
        // an assumption as a measurement.
        value={dashMoney(`${card.giveBackApprox ? '≈' : ''}${unitCompact(-card.giveBack, unit)}`)}
        cls={nothingCounted ? '' : (card.giveBackN ? 'red' : 'green')}
        sub={nothingCounted
          ? 'nothing counted today'
          : (card.giveBackN
            ? `left on the table · ${card.giveBackN} trade${card.giveBackN === 1 ? '' : 's'}`
            : 'no give-back')}
        title="Give-back from the in-trade peak, weighted by the size actually on at the peak when the trade carries a fill timeline. A ≈ marks a total that includes a trade with no timeline — for those it assumes the full position was on at the peak, an upper bound."
      />
      {/* ⚠ NO TOOLTIP, and therefore no ⓘ — beta passes FOUR arguments to this
          tile and five to Give-Back. Verified in beta's live DOM rather than
          inferred: `Cost of Give-Back ⓘ` carries a title, `Controllable Losses`
          does not.

          One was added here unasked, explaining that this tile counts EVERY
          trade including excluded accounts. That IS worth saying — it is the
          D77/D80 population split and it will surprise someone — but it is an
          addition, not a port, so it goes back only if Josh asks for it. The
          explanation lives in `domain/dayReport.ts` meanwhile. */}
      <Tile
        label="Controllable Losses"
        value={unitCompact(-card.controllableLoss, unit)}
        cls={card.controllableLoss > 0 ? 'red' : 'green'}
        sub={card.controllableLoss > 0 ? 'avoidable damage' : 'none flagged'}
      />
    </div>
  );
}

// ── The intraday curve ───────────────────────────────────────────────────────

// ⚠ `padL` 58, matching beta — the y labels are money and `-$1,625` needs the
// room. 54 clipped them.
const CH = { w: 1000, h: 300, padL: 58, padR: 16, padT: 14, padB: 26 };

/**
 * Hand-rolled SVG (D81) — this client has no chart library and is not adding one
 * for a single surface.
 *
 * ⚠ THE HEADER'S "TROUGH" IS `-maxDD`, NOT `low` AND NOT `heatLow`. Three
 * different numbers, and an earlier transcription named the wrong one: the red
 * MAE envelope visibly reaches lower than the stated trough, which is exactly
 * what a peak-to-trough FALL in realized equity does against a low that includes
 * open-position heat.
 *
 * ── WHAT THE FIRST VERSION GOT WRONG, ALL OF IT VISIBLE SIDE BY SIDE ──
 *
 * It drew an orange line with no fill, two axis labels and no gridlines. Beta
 * draws a SIGN-COLOURED line over a gradient fill, five nice-numbered gridlines,
 * a ring at every envelope point, and a dot per trade coloured by that trade's
 * own result. None of it changes a number; all of it changes whether the chart
 * can be read at a glance, which is the only reason the card exists.
 *
 * ⚠ ONE DELIBERATE DIVERGENCE: beta sets `preserveAspectRatio="none"` and puts
 * its axis labels INSIDE the svg, so they stretch with the container — its own
 * screenshot shows the bottom-left y label and the first x label overlapping
 * into an unreadable smear. This keeps the default aspect ratio so the text
 * stays legible.
 */
function IntradayCard(
  { curve, tzLabel, unit }:
  { curve: IntradayCurve | null; tzLabel: string; unit: PnlUnit },
) {
  const gid = useId();

  if (!curve || !curve.points.length) {
    return (
      <div className="card">
        <div className="dash-card-head">
          <h3>Cumulative P&L — Intraday</h3>
        </div>
        <p className="muted">No time-stamped trades on this day.</p>
      </div>
    );
  }

  const { steps, tMin, tMax } = stepAndHold(curve.points);

  // ⚠ The domain spans the ENVELOPE, not just realized equity — otherwise the
  // MAE band is clipped at the axis and a day that sat deeply underwater looks
  // like it never did.
  let lo = Math.min(0, curve.heatLow), hi = Math.max(0, curve.maxGain);
  for (const p of curve.points) {
    if (p.peak != null) hi = Math.max(hi, p.peak);
    if (p.trough != null) lo = Math.min(lo, p.trough);
  }
  // ⚠ 10% HEADROOM EACH WAY, as beta does. Without it the extreme point sits
  // exactly on the frame and reads as clipped rather than as the peak.
  const headroom = (hi - lo || 1) * 0.1;
  lo -= headroom;
  hi += headroom;

  const span = hi - lo || 1;
  const x = (m: number) => CH.padL + ((m - tMin) / (tMax - tMin || 1)) * (CH.w - CH.padL - CH.padR);
  const y = (v: number) => CH.padT + (1 - (v - lo) / span) * (CH.h - CH.padT - CH.padB);
  const floor = CH.h - CH.padB;
  // 🔴 CLIPPED TO THE PLOTTED DOMAIN, and that filter is not cosmetic.
  // `niceTicks` rounds OUTWARD on purpose — `Math.floor(min/step)*step` and
  // `Math.ceil(max/step)*step` — so the outermost ticks it returns routinely sit
  // BEYOND `lo`/`hi`. Their gridlines then draw above the top of the plot or
  // below its floor, and their labels hang off the canvas: measured in
  // production 2026-09-17, "+$2,000" was sliced across the top and a "-$500"
  // sat at y=329 in a 300-tall viewBox.
  //
  // ⚠ FIXED HERE, NOT IN `niceTicks`. Rounding outward is correct and four other
  // charts depend on it; they simply have the headroom to absorb it.
  const ticks = niceTicks(lo, hi, 5).filter((t) => t >= lo && t <= hi);

  const path = steps.map((s, i) => `${i ? 'L' : 'M'}${x(s.mins).toFixed(1)},${y(s.equity).toFixed(1)}`).join('');
  // ⚠ The fill closes down to the FLOOR, not to the zero line — beta's does the
  // same, so a losing day's band hangs below zero rather than inverting.
  const area = `${path}L${x(tMax).toFixed(1)},${floor}L${x(tMin).toFixed(1)},${floor}Z`;

  // ⚠ Both envelopes START at the left edge on zero, so the first trade's heat
  // reads as a departure from flat rather than as a line beginning mid-air.
  const envelope = (key: 'peak' | 'trough') => {
    const pts = curve.points.filter((p) => p[key] != null);
    if (!pts.length) return '';
    return [{ mins: tMin, v: 0 }, ...pts.map((p) => ({ mins: p.mins, v: p[key] as number }))]
      .map((p, i) => `${i ? 'L' : 'M'}${x(p.mins).toFixed(1)},${y(p.v).toFixed(1)}`)
      .join('');
  };

  // ⚠ SIGN-COLOURED on the day's FINAL equity, which is what beta keys on — not
  // the brand colour. A red curve is the fastest read on the page.
  const last = curve.points[curve.points.length - 1];
  const tone = (last ? last.equity : 0) >= 0 ? 'up' : 'down';
  // 🔴 THE PAINT IS ON THE ELEMENTS BELOW, NOT IN A STYLESHEET, and that is not
  // a style preference — see `svgSnapshotSafe.test.ts`. The snapshot rasteriser
  // clones this SVG without carrying class rules onto its children, so a chart
  // coloured by CSS renders as a solid black rectangle in the exported PNG while
  // looking perfect on screen. `var(--token)` in an attribute IS carried
  // (measured against a Dashboard export), so the palette still comes from one
  // place. This used to route through a `--dv-tone` custom property, which is
  // the part that did not survive.
  const toneColor = tone === 'up' ? 'var(--green)' : 'var(--red)';
  const xTicks = [tMin, (tMin + tMax) / 2, tMax];

  return (
    <div className="card">
      <div className="dash-card-head">
        <h3>Cumulative P&L — Intraday</h3>
        <span className="section-tag">
          peak {unitCompact(curve.maxGain, unit)} · trough {unitCompact(-curve.maxDD, unit)}
        </span>
      </div>

      {/* ⚠ A LEGEND, because three lines with no key is a chart nobody can read
          — and the two dashed ones are not intuitive: one is the best the trade
          reached, the other the worst heat it took while open. */}
      <div className="dayview-legend">
        <span><i className={`lg-solid lg-${tone}`} />Realized equity</span>
        {curve.hasEnvelope ? (
          <>
            <span><i className="lg-dash lg-up" />Best reached (MFE) · exit give-back</span>
            <span><i className="lg-dash lg-down" />Heat taken (MAE) · entry risk</span>
          </>
        ) : null}
      </div>

      {/* ⚠ `tone-up`/`tone-down` USED TO BE ON THIS TAG and are gone with the
          CSS that read them. They set a `--dv-tone` custom property the painted
          children consumed — the exact indirection the snapshot rasteriser
          drops. The tone now reaches the elements through `toneColor`. */}
      <svg
        className="dayview-chart" viewBox={`0 0 ${CH.w} ${CH.h}`}
        role="img" aria-label="Cumulative profit and loss through the day"
      >
        <defs>
          {/* ⚠ `useId` — two of these on one page (a modal over the tab) would
              otherwise share a gradient id and the second would take the
              first's colours. */}
          <linearGradient id={`dv-fill-${gid}`} x1="0" y1="0" x2="0" y2="1">
            {/* ⚠ THESE TWO WERE THE BLACK RECTANGLE. A `<stop>` with no
                stop-color is BLACK at FULL opacity — the two defaults compound,
                so losing the rule did not fade the fill, it filled the chart. */}
            <stop offset="0%" stopColor={toneColor} stopOpacity={0.24} />
            <stop offset="100%" stopColor={toneColor} stopOpacity={0} />
          </linearGradient>
        </defs>

        {/* ⚠ Nice-numbered gridlines, and the ZERO line is drawn differently
            from the rest — it is the only one that means anything. */}
        {ticks.map((t) => {
          const isZero = Math.abs(t) < 1e-6;
          return (
            <line
              key={`g${t}`} x1={CH.padL} x2={CH.w - CH.padR} y1={y(t)} y2={y(t)}
              stroke={isZero ? 'var(--border-2)' : 'var(--border-1)'}
              strokeWidth={isZero ? 1 : 0.5}
            />
          );
        })}

        <path d={area} fill={`url(#dv-fill-${gid})`} />

        {curve.hasEnvelope ? (
          <>
            <path
              d={envelope('peak')} fill="none"
              stroke="var(--green)" strokeWidth={1.5} strokeDasharray="4 3" opacity={0.8}
            />
            <path
              d={envelope('trough')} fill="none"
              stroke="var(--red)" strokeWidth={1.5} strokeDasharray="4 3" opacity={0.8}
            />
            {/* Hollow rings, so they read as reference points rather than as
                data the equity line passed through. */}
            {/* ⚠ 🚨 KEYED BY INDEX, AND THE PREVIOUS KEY WAS A REAL BUG.
                It was `e${p.mins}` — the minute the trade closed — so two trades
                closing in the SAME MINUTE collided. React logged "Encountered
                two children with the same key… may cause children to be
                duplicated and/or omitted" and it means it: one of the two
                trades' MFE/MAE rings can silently not render.

                Josh's own book triggers it on two minutes (68 and 599), which is
                how it was found — in the CONSOLE of the running app, not by any
                test. The suite renders the chart and asserts its geometry; a
                dropped ring is still valid geometry.

                The index is the right key here because this array is never
                reordered, filtered or keyed off identity — it is the day's
                trades in time order, rebuilt whenever the day changes. */}
            {/* ⚠ `<title>` IS THE HOVER TEXT, and it is beta's mechanism too
                (app/index.html:28197-28200) — a child of the shape, rendered by
                the browser as a native tooltip. No library, no hover state, and
                it survives a screenshot of the region because it is markup.

                🎯 The ring is the only thing on this chart that says what the
                trade DID between entry and exit. Without it a reader sees two
                hollow circles and no way to learn what they mean.

                ⚠ `unitPlain`, NOT `unitValue`, on all three figures here. These
                are MAGNITUDES — how far it ran, what was left behind, how much
                heat was taken — and none of them is a gain. `unitValue` would
                print "MAE +$10.00 heat taken", which is the same defect TCR-15
                just fixed on the Coach's Report: a plus sign turning a cost into
                something that reads like a profit. The dot below keeps the
                signed formatter, because a trade's P&L and the running total do
                have a direction. */}
            {curve.points.map((p, i) => (
              <g key={i}>
                {/* ⚠ NO `strokeDasharray` ON THE RINGS — they are hollow and
                    solid. They used to inherit the envelope's dash through a
                    shared class and needed `.dv-ring` to cancel it, or they drew
                    as four disconnected arcs. With the paint on the element
                    there is nothing to cancel. */}
                {p.peak != null
                  ? (
                    <circle
                      cx={x(p.mins)} cy={y(p.peak)} r={2.4} fill="none"
                      stroke="var(--green)" strokeWidth={1.3} data-ring="mfe"
                    >
                      <title>
                        MFE {unitPlain(p.peak - p.eBefore, unit, 2)} · left{' '}
                        {unitPlain(p.peak - p.equity, unit, 2)} on the table
                      </title>
                    </circle>
                  )
                  : null}
                {p.trough != null
                  ? (
                    <circle
                      cx={x(p.mins)} cy={y(p.trough)} r={2.4} fill="none"
                      stroke="var(--red)" strokeWidth={1.3} data-ring="mae"
                    >
                      <title>MAE {unitPlain(p.eBefore - p.trough, unit, 2)} heat taken</title>
                    </circle>
                  )
                  : null}
              </g>
            ))}
          </>
        ) : null}

        <path d={path} fill="none" stroke={toneColor} strokeWidth={2} />

        {/* ⚠ Each dot takes ITS OWN trade's colour, not the day's. A green day
            with one bad trade should show that trade red. */}
        {/* ⚠ Same fix as the rings above. `d${p.mins}-${p.equity}` survived only
            because cumulative equity happened to differ — two same-minute trades
            netting to the same running total would collide, and a scratch pair
            does exactly that. */}
        {/* ⚠ EACH DOT TAKES ITS OWN TRADE'S SIGN, not the day's — a losing
            trade inside a winning day is the thing worth seeing. The ring is the
            card background, so a dot sitting on the line still reads as a
            separate mark. */}
        {curve.points.map((p, i) => (
          <circle
            key={i} cx={x(p.mins)} cy={y(p.equity)} r={3}
            fill={p.pnl >= 0 ? 'var(--green)' : 'var(--red)'}
            stroke="var(--bg-2)" strokeWidth={1}
            data-dot={p.pnl >= 0 ? 'up' : 'down'}
          >
            {/* ⚠ THE TIMEZONE TRAVELS WITH THE CLOCK TIME, as it does on the
                axis below. A bare "09:31" on a screen whose day boundary is
                configurable is ambiguous, and the tooltip is the one place a
                reader sees a single trade's time rather than a scale. */}
            <title>
              {minsToHm(Math.round(p.mins))} {tzLabel} · {p.contract} {p.side} ·{' '}
              {unitValue(p.pnl, unit)} (run {unitValue(p.equity, unit)})
            </title>
          </circle>
        ))}

        {/* 🔴 `fontSize` IS LOAD-BEARING, NOT DECORATION. Without it these render
            at the 16px default instead of 10px, overflow the chart box and are
            clipped by the viewBox — which is how the exported PNG came to show
            "$1,500" with no "+" and "13:55 /" with no "ET". Those read as a
            separate layout bug and are the same one. */}
        {ticks.map((t) => (
          <text
            key={`t${t}`} x={CH.padL - 8} y={y(t) + 3.5} textAnchor="end" data-axis="y"
            fill="var(--text-2)" fontSize={10} fontFamily="var(--mono)"
          >
            {unitCompact(t, unit)}
          </text>
        ))}
        {/* ⚠ The TIMEZONE is on the axis, as in beta. A clock time with no zone
            on a screen whose day boundary is configurable is ambiguous. */}
        {/* 🔴 THE LAST LABEL ANCHORS TO ITS END, not its middle. Centred, it sat
            on x = w − padR = 984 and an 8-character time reached past 1000 —
            production rendered "15:20 ET" as "15:20 E", one character eaten by
            the canvas edge.

            ⚠ The FIRST label is deliberately left centred. It sits at padL = 58
            with roughly 34px of clearance, so it does not clip, and anchoring it
            to `start` would shove it visibly right of the tick it labels — a
            cosmetic change with no defect behind it. Widening `padR` was the
            other option and was rejected: it narrows the plot on every day to
            fix the last label on some. */}
        {xTicks.map((t, i) => (
          <text
            key={`x${t}`} x={x(t)} y={CH.h - CH.padB + 17} data-axis="x"
            textAnchor={i === xTicks.length - 1 ? 'end' : 'middle'}
            fill="var(--text-2)" fontSize={10} fontFamily="var(--mono)"
          >
            {minsToHm(Math.round(t))} {tzLabel}
          </text>
        ))}
      </svg>
    </div>
  );
}

// ── Prop rails ───────────────────────────────────────────────────────────────

function Rail(
  { label, pct: p, tone, sub }:
  { label: string; pct: number; tone: string; sub: string },
) {
  return (
    <div className="dayview-rail">
      <div className="dayview-rail-head">
        <span className="muted">{label}</span>
        <span className={`dayview-rail-pct ${tone}`}>{Math.round(p)}%</span>
      </div>
      <div className="progress">
        <div className={`progress-fill ${tone}`} style={{ width: `${Math.max(0, Math.min(100, p))}%` }} />
      </div>
      <div className="muted dayview-rail-sub">{sub}</div>
    </div>
  );
}

/**
 * ── ⚠ ARCHIVED AND EXCLUDED ACCOUNTS HAVE NO LIVE RAILS ──
 *
 * A blown evaluation still carries a daily loss limit in its rules, and summing
 * it into the denominator makes a live account's usage read as a fraction of a
 * limit that no longer applies — the optimistic direction. Beta excludes both,
 * in "all accounts" mode and when one is explicitly picked.
 */
/**
 * 🚨 EVERY FIGURE IN HERE STAYS IN DOLLARS, WHATEVER THE UNIT SWITCH SAYS.
 *
 * These are the PROP FIRM’S rules: the daily loss limit, the profit target, the
 * consistency cap. The firm states them in dollars and enforces them in dollars.
 * A drawdown buffer rendered as "-320 ticks" is not a rule anybody has, and a
 * trader who sizes off it can fail an account on a number this app invented.
 *
 * ⚠ So there is no `unit` prop here on purpose, which also makes it impossible
 * to convert these by accident in a later pass.
 *
 * 🚨 AND THAT GUARANTEE LEAKED ONCE ALREADY (#67). The daily-loss-limit rail
 * takes the day's close as a NUMBER, and the number it was handed was the
 * scorecard's — already converted to the display unit. On Points it printed
 * "closed $-28" beside "of $-2,000": a points figure wearing a dollar sign,
 * against a real risk limit. Having no `unit` prop does not help when a
 * pre-converted value walks in through a different door, so the prop is named
 * `dayPnlUsd` — the unit is in the name, and a wrong value has to be renamed
 * to get in.
 */
function PropRails(
  { accounts, selection, allRows, curve, dayPnlUsd, today, mode }: {
    accounts: readonly Account[] | null;
    selection: readonly string[];
    allRows: readonly { accountId: string | null }[] | null;
    curve: IntradayCurve | null;
    /** ⚠ DOLLARS, never the display unit. See the header. */
    dayPnlUsd: number;
    today: string;
    mode: 'calendar' | 'session' | null;
  },
) {
  const accts = useMemo(() => (accounts ?? []).filter(
    (a) => !a.archived && !a.excludeFromMetrics
      && (selection.length === 0 || selection.includes(String(a.id))),
  ), [accounts, selection]);

  if (!accounts || !mode) return <div className="card"><p className="muted">Loading…</p></div>;

  const rails: ReactElement[] = [];
  const prefix = accts.length > 1 ? true : false;
  const trades = allRows ?? [];

  // ── Daily loss limit ──
  // ⚠ MEASURED AGAINST THE OPEN-EQUITY LOW, in DOLLARS. The rule is a firm
  // dollar rule — Apex does not care that you are looking at points — and it is
  // scored against the worst the account went INCLUDING unrealized heat, not
  // against where it closed. `heatLowUsd`, never `low`.
  const dllTotal = accts.reduce((s, a) => s + (Number(a.rules?.dailyLossLimit) || 0), 0);
  if (dllTotal > 0 && curve) {
    const worst = Math.max(0, -(curve.heatLowUsd ?? curve.lowUsd));
    const p = Math.min(100, (worst / dllTotal) * 100);
    rails.push(
      <Rail
        key="dll"
        label="Daily Loss Limit · at the open-equity low"
        pct={p}
        tone={p >= 80 ? 'red' : p >= 50 ? 'amber' : 'green'}
        sub={`low ${money(-worst, 0)} (incl. open heat) of ${money(-dllTotal, 0)} · closed ${money(dayPnlUsd, 0)}`}
      />,
    );
  }

  // ── Profit target ──
  // ⚠ PER ACCOUNT, summed — never one date range. Each account has its own
  // attempt boundary, and beta's comment records what a hardcoded epoch did:
  // "$4,800 of $3,000, 100%" on an account the Accounts tab reported at 0%.
  const ptAccts = accts.filter((a) => (Number(a.rules?.profitTarget) || 0) > 0);
  const ptTotal = ptAccts.reduce((s, a) => s + (Number(a.rules?.profitTarget) || 0), 0);
  if (ptTotal > 0) {
    const soFar = ptAccts.reduce(
      (s, a) => s + (computeProfitTargetProgress(a, trades as never, today, mode).profitSoFar ?? 0),
      0,
    );
    const p = Math.max(0, Math.min(100, (soFar / ptTotal) * 100));
    rails.push(
      <Rail
        key="pt" label="Profit Target · cumulative" pct={p} tone="green"
        sub={`${money(soFar, 0)} of ${moneyPlain(ptTotal, 0)} target`}
      />,
    );
  }

  // ── Per-account payout gates ──
  // ⚠ NOT AGGREGATABLE, unlike the two above: a consistency cap is a share of
  // ONE account's own profit, so summing two accounts' best days against their
  // combined total would report compliance neither of them has.
  for (const a of accts) {
    const pool = tradesInAttempt(a, trades as never, today, mode);
    const name = prefix ? `${a.name || 'account'} · ` : '';

    const c = computeConsistencyMath(a, trades as never, today, mode);
    if (c && c.totalProfit > 0) {
      const p = Math.min(100, (c.bestDayPct / c.thresholdPct) * 100);
      rails.push(
        <Rail
          key={`c-${a.id}`}
          label={`${name}Consistency · single-day cap`}
          pct={p}
          tone={c.inCompliance ? 'green' : 'red'}
          sub={`largest day ${money(c.bestDay, 0)} = ${Math.round(c.bestDayPct)}% of ${money(c.totalProfit, 0)} profit · cap ${c.thresholdPct}%${c.inCompliance ? '' : ' — OVER'}`}
        />,
      );
    }

    const minD = Number(a.rules?.minTradingDays) || 0;
    if (minD > 0) {
      const d = daysTraded(pool as never, mode);
      const met = d >= minD;
      rails.push(
        <Rail
          key={`m-${a.id}`}
          label={`${name}Min trading days`}
          pct={Math.min(100, (d / minD) * 100)}
          tone={met ? 'green' : 'amber'}
          sub={`${d} of ${minD} day${minD === 1 ? '' : 's'} traded${met ? ' — met' : ''}`}
        />,
      );
    }
  }

  return (
    <div className="card">
      <div className="dash-card-head">
        <h3>Prop Rails</h3>
        <span className="section-tag">
          {accts.length === 1 ? (accts[0]?.name || 'account') : `${accts.length} accounts`}
        </span>
      </div>
      {rails.length ? rails : (
        // ⚠ NAMES THE FIX. "No rules set" alone reads as a broken card; saying
        // where to set them makes it an empty state the user can act on.
        <p className="muted">
          No prop-firm rules set on the active account(s). Set a daily loss limit,
          profit target, consistency rule or min trading days on the Accounts tab
          to track them here.
        </p>
      )}
    </div>
  );
}

// ── Performance strip ────────────────────────────────────────────────────────

function PerformanceStrip(
  { k, curve, counted, winRateDelta }: {
    k: ReturnType<typeof selectKpis> | null;
    curve: IntradayCurve | null;
    counted: readonly TradeTableRow[];
    winRateDelta: number | null;
  },
) {
  if (!k) return null;
  const mins = avgMinutesInTrade(counted);
  /**
   * ── 🚨 NOTHING COUNTED IS NOT THE SAME AS ZERO ──
   *
   * With an empty counted set every figure here computes to a clean, plausible
   * number: **Win Rate 0%, Profit Factor 0.00, Avg Win $0.00, Max DD $0.00**.
   * None of them is `NaN`, so nothing looks broken — and "0% win rate" reads as
   * **"you lost every trade today"**, which is a far worse lie than a blank.
   *
   * ⚠ THE AUTOMATED SWEEP FOR BAD NUMBERS COULD NOT SEE THIS. It looked for
   * `NaN`, `Infinity` and `∞`; these are none of those. **A screenshot of the
   * real 2026-08-05 is what caught it**, which is the same lesson this project
   * keeps relearning.
   *
   * An em-dash is the honest rendering, and the tab already uses one for
   * Avg Time and `N/A` in the by-account table for exactly this idea.
   */
  const nothingCounted = counted.length === 0;
  const dash = (v: string): string => (nothingCounted ? '—' : v);
  // ⚠ SEVEN cells, and NOT the Dashboard's eight — composition is a parity
  // claim (D85). This strip has Profit Factor, Max DD and Avg Time, which the
  // Dashboard's does not, and drops both capture tiles, which live above.
  const cells: [string, string, string, string][] = [
    ['Win Rate', dash(pct(k.winRate, 0)), '', nothingCounted || winRateDelta == null ? '' : `${winRateDelta >= 0 ? '+' : ''}${num(winRateDelta, 0)} vs all-time`],
    ['Profit Factor', dash(k.profitFactor === Infinity ? '∞' : num(k.profitFactor, 2)), '', '$ won per $1 lost'],
    ['Avg R:R', dash(`${num(k.avgRR, 2)}R`), '', 'realized R / trade'],
    // ⚠ The colour goes too. A grey em-dash is "no data"; a GREEN em-dash still
    // reads as a result, just an unreadable one.
    // ⚠ `k.unit` — the unit the KPIs were COMPUTED in, echoed by `computeKpis`
    // so a tile cannot mislabel its own number. The curve is built in the same
    // unit, so Max DD reads it from the same place rather than a second source.
    ['Avg Win', dash(unitValue(k.avgWin, k.unit)), nothingCounted ? '' : 'green', ''],
    ['Avg Loss', dash(unitValue(k.avgLoss, k.unit)), nothingCounted ? '' : 'red', ''],
    ['Max DD', dash(unitValue(-(curve?.maxDD ?? 0), k.unit)), nothingCounted ? '' : 'red', 'intraday'],
    ['Avg Time', mins == null ? '—' : `${Math.round(mins)}m`, '', 'in trade'],
  ];
  return (
    <div className="trade-stats-strip" data-testid="dayview-strip">
      {cells.map(([label, value, cls, sub]) => (
        <div className="stat-cell" key={label}>
          <div className="slabel">{label}</div>
          <div className={`sval ${cls}`}>{value}</div>
          {sub ? <div className="ssub">{sub}</div> : null}
        </div>
      ))}
    </div>
  );
}

// ── By account ───────────────────────────────────────────────────────────────

function ByAccount(
  { accounts, dayTrades, contracts, allRows, today, mode, unit }: {
    accounts: readonly Account[] | null;
    unit: PnlUnit;
    dayTrades: readonly TradeTableRow[] | null;
    contracts: readonly { symbol: string }[] | null;
    allRows: readonly { accountId: string | null }[] | null;
    today: string;
    mode: 'calendar' | 'session' | null;
  },
) {
  const rows = useMemo<DayAccountRow[] | null>(() => {
    if (!accounts || !dayTrades || !contracts || !mode) return null;
    // ⚠ THE GLOBAL ACCOUNT FILTER IS DELIBERATELY NOT APPLIED — see the header.
    // Nor is the facet filter: this table answers "what did each account do
    // today", and a pattern filter would make an account that traded read N/A.
    const active = accounts.filter((a) => !a.archived && !a.excludeFromMetrics);
    return dayAccountRows(
      active.map((a) => ({ id: String(a.id), name: a.name })),
      dayTrades as never,
      unit,
      contracts as never,
      (id) => {
        const a = active.find((x) => String(x.id) === id);
        if (!a || !(Number(a.rules?.profitTarget) || 0)) return null;
        return computeProfitTargetProgress(a, (allRows ?? []) as never, today, mode).pct;
      },
    );
  }, [accounts, dayTrades, contracts, allRows, today, mode, unit]);

  if (!rows) return <div className="card"><p className="muted">Loading…</p></div>;

  return (
    <div className="card dayview-byaccount">
      <p className="muted dayview-byaccount-cap">
        All active accounts · day stats (N/A = no trades this day) · progress =
        cumulative vs profit target
      </p>
      <table className="table">
        <thead>
          <tr>
            <th>Account</th>
            <th className="num">Total Trades</th>
            <th className="num">Win Rate</th>
            <th className="num">Profit Factor</th>
            <th>Account Progress</th>
            <th className="num">Commissions</th>
            <th className="num">Total P&L</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.accountId}>
              <td>{r.name}</td>
              {r.traded ? (
                <>
                  <td className="num">{r.n}</td>
                  <td className="num">{r.winRate == null ? '—' : pct(r.winRate, 0)}</td>
                  <td className="num">{r.profitFactor === Infinity ? '∞' : num(r.profitFactor, 2)}</td>
                </>
              ) : (
                // ⚠ `N/A`, not an em dash and not 0. The account did not trade,
                // which is different from "traded and scored nothing".
                <>
                  <td className="num muted">N/A</td>
                  <td className="num muted">N/A</td>
                  <td className="num muted">N/A</td>
                </>
              )}
              <td>
                {r.progressPct == null ? (
                  <span className="muted dayview-notarget">no target</span>
                ) : (
                  <span className="dayview-prog">
                    <span className="dayview-prog-track">
                      <span className="dayview-prog-fill" style={{ width: `${r.progressPct.toFixed(0)}%` }} />
                    </span>
                    <span className="muted">{r.progressPct.toFixed(0)}%</span>
                  </span>
                )}
              </td>
              {r.traded ? (
                <>
                  {/* ⚠ FEES STAY IN DOLLARS while the P&L beside them follows the
                      unit. A commission is a dollar amount the broker charged;
                      it has no points value, and beta keeps fees in dollars for
                      exactly this reason. */}
                  <td className="num">{money(r.fees)}</td>
                  <td className={`num ${pnlClass(r.pnl)}`}>{unitValue(r.pnl, unit)}</td>
                </>
              ) : (
                <>
                  <td className="num muted">N/A</td>
                  <td className="num muted">N/A</td>
                </>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Breakdowns ───────────────────────────────────────────────────────────────

function patternRows(
  counted: readonly TradeTableRow[],
  patterns: readonly { id: string; name: string }[] | null,
  valueOf: (t: TradeTableRow) => number | null,
): { key: string; value: number }[] {
  const sums = new Map<string, number>();
  for (const t of counted) {
    const pid = t.patternId ? String(t.patternId) : '';
    if (!pid) continue;
    const v = valueOf(t);
    // ⚠ SKIP, DO NOT COERCE TO ZERO — beta's own rule (app/index.html:28581-2).
    // A pattern whose trades have no value in the chosen unit (R with no stop
    // recorded, ticks on a contract missing from the catalogue) gets NO ROW,
    // rather than a row reading "0.00R" that claims the setup scratched. Do not
    // "tidy" this back to `?? 0`.
    if (v == null) continue;
    sums.set(pid, (sums.get(pid) ?? 0) + v);
  }
  return [...sums.entries()]
    .map(([pid, value]) => ({
      // ⚠ 'Untitled' rather than the raw id, matching beta. A row labelled with
      // a uuid is not a breakdown anybody can read.
      key: (patterns ?? []).find((p) => String(p.id) === pid)?.name ?? 'Untitled',
      value,
    }))
    .sort((a, b) => b.value - a.value);
}
